import React, { useState } from 'react';
import { Box, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { colors } from '../../../../styles/theme';
import {
  BlockCollapseToggle,
  BlockHeader,
  PUBLICATION_BLOCK_SX,
} from './formBlockUi';
import {
  buildOfficialTaxPortalLink,
  formatFiscalArs,
  formatOfficialBuiltSurface,
  formatValuationPeriod,
  getOfficialUnitNumber,
} from '../../../utils/lotFunctionalUnits';

const rowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 1.5,
};

const labelSx = {
  fontSize: '12px',
  color: colors.textSecondary,
  flexShrink: 0,
};

const valueSx = {
  fontSize: '13px',
  color: colors.textPanelTitle,
  textAlign: 'right',
  minWidth: 0,
  wordBreak: 'break-word',
};

const CadastreRow = ({ label, children }) => {
  if (!children) return null;
  return (
    <Box sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography component="div" sx={valueSx}>{children}</Typography>
    </Box>
  );
};

/**
 * Datos catastrales de una UF oficial (solo lectura). Sirve en el formulario
 * de overlay y en vistas compartidas cuando viene `official_unit`.
 * Colapsable y cerrado por defecto (mismo toggle que "Solo en tu espacio");
 * `defaultExpanded` para abrirlo de entrada.
 */
const OfficialUnitCadastralBlock = ({
  officialUnit,
  compact = false,
  province = null,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!officialUnit) return null;

  const unitNumber = getOfficialUnitNumber(officialUnit);
  const taxPortal = buildOfficialTaxPortalLink({
    taxAccount: officialUnit.tax_account,
    province,
  });
  const surface = formatOfficialBuiltSurface(officialUnit.built_surface_m2);
  const fiscalTotal = formatFiscalArs(officialUnit.fiscal_total_ars);
  const fiscalLand = formatFiscalArs(officialUnit.fiscal_land_ars);
  const fiscalImprovements = formatFiscalArs(officialUnit.fiscal_improvements_ars);
  const period = formatValuationPeriod(officialUnit.valuation_period);

  return (
    <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 2 }}>
      <BlockHeader
        icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18, color: colors.textSecondary, mt: 0.1 }} />}
        title={unitNumber ? `UF ${unitNumber}` : 'Datos catastrales'}
        subtitle="Solo lectura · Información catastral oficial"
        action={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {taxPortal ? (
              <Tooltip title={taxPortal.label} placement="top" arrow>
                <IconButton
                  size="small"
                  href={taxPortal.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  aria-label={taxPortal.label}
                  sx={{
                    p: 0.25,
                    color: colors.primary,
                    '&:hover': {
                      color: colors.primaryHover,
                      bgcolor: 'transparent',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                      color: 'inherit',
                    },
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <BlockCollapseToggle
              expanded={expanded}
              onToggle={() => setExpanded((v) => !v)}
              labelExpanded="Contraer datos catastrales"
              labelCollapsed="Expandir datos catastrales"
            />
          </Box>
        )}
      />
      <Collapse in={expanded}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
        <CadastreRow label="Cuenta">
          {officialUnit.tax_account || null}
        </CadastreRow>
        <CadastreRow label="Superficie edificada">{surface}</CadastreRow>
        {!compact && (
          <>
            <CadastreRow label="Valuación fiscal">{fiscalTotal}</CadastreRow>
            <CadastreRow label="Tierra">{fiscalLand}</CadastreRow>
            <CadastreRow label="Mejoras">{fiscalImprovements}</CadastreRow>
          </>
        )}
        <CadastreRow label="Período">{period}</CadastreRow>
      </Box>
      </Collapse>
    </Box>
  );
};

export default OfficialUnitCadastralBlock;
